/**
 * ClinicView — режим просмотра тарифа клиники: карточки пакетов (активный
 * подсвечен калькулятором), нота гибридной ступени «Сеть» и калькулятор-бар.
 */

import {Plus} from 'lucide-react';

import {type ClinicPackage, clinicQuoteFor} from '@services/clients';
import type {ClinicTariff} from '@services/tariffs';
import {formatNumber} from '@utils/format';

import {CalcIcon} from '../../CalcIcon';
import {SeatStepper} from '../../SeatStepper';
import {explainQuote} from '../../Tariffs.utils';

type ClinicViewProps = {
  clinic: ClinicTariff;
  calc: number;
  setCalc: (n: number) => void;
};

export const ClinicView = ({clinic, calc, setCalc}: ClinicViewProps) => {
  const quote = clinicQuoteFor(calc, clinic.packages, clinic.overPrice);
  return (
    <>
      <div className="mt-5 grid grid-cols-3 gap-3.5">
        {clinic.packages.map((p, i) => (
          <PackageCard
            key={p.name + i}
            pkg={p}
            lo={i === 0 ? 1 : clinic.packages[i - 1].upTo + 1}
            active={quote.over === 0 && quote.pkgIndex === i}
            popular={i === 1}
          />
        ))}
      </div>

      <NetworkNote packages={clinic.packages} overPrice={clinic.overPrice} />

      <div className="mt-4 flex items-center gap-[18px] rounded-[13px] border border-skr-info-border bg-skr-info px-[17px] py-[15px]">
        <div className="flex shrink-0 items-center gap-2 text-[13px] font-semibold text-skr-info-ink">
          <CalcIcon />
          Проверить
        </div>
        <SeatStepper seats={calc} setSeats={setCalc} size="sm" />
        <div className="flex-1 text-[12.5px] text-skr-info-ink2">
          {explainQuote(calc, clinic.packages, clinic.overPrice)}
        </div>
        <div className="shrink-0 text-right">
          <span className="font-mono text-[20px] font-semibold text-skr-info-ink">
            {formatNumber(quote.sum)}
          </span>{' '}
          <span className="text-xs text-skr-info-faint">сом/мес</span>
        </div>
      </div>
    </>
  );
};

const PackageCard = ({
  pkg,
  lo,
  active,
  popular,
}: {
  pkg: ClinicPackage;
  lo: number;
  active: boolean;
  popular: boolean;
}) => (
  <div
    className={`relative rounded-[13px] border-[1.5px] px-4 pb-[15px] pt-4 ${
      active
        ? 'border-primary bg-skr-info'
        : popular
          ? 'border-skr-info-line bg-card'
          : 'border-skr-line bg-card'
    }`}
  >
    {popular && (
      <div className="absolute -top-2.5 left-4 rounded-full bg-primary px-[9px] py-[3px] text-[10.5px] font-semibold uppercase tracking-[.04em] text-primary-foreground">
        Популярный
      </div>
    )}
    <div className={`text-[15px] font-bold -tracking-[.01em] ${active ? 'text-primary' : ''}`}>
      {pkg.name}
    </div>
    <div className={`mt-0.5 text-xs font-medium ${active ? 'text-primary' : 'text-skr-label'}`}>
      {lo}–{pkg.upTo} врача
    </div>
    <div className="mt-3 flex items-baseline gap-[5px]">
      <span className="font-mono text-[26px] font-semibold -tracking-[.02em]">
        {formatNumber(pkg.price)}
      </span>
      <span className="text-xs text-muted-foreground">сом/мес</span>
    </div>
  </div>
);

const NetworkNote = ({
  packages,
  overPrice,
}: {
  packages: readonly ClinicPackage[];
  overPrice: number;
}) => {
  const last = packages[packages.length - 1];
  return (
    <div className="mt-3.5 flex items-center gap-2.5 rounded-[11px] border border-skr-line2 bg-skr-head px-[15px] py-3">
      <div className="grid size-8 shrink-0 place-items-center rounded-[9px] bg-accent text-primary">
        <Plus className="size-[17px]" strokeWidth={1.9} />
      </div>
      <div className="flex-1 text-[12.5px] text-muted-foreground">
        <span className="text-[13px] font-bold text-foreground">Сеть</span> · 11+ врачей — базовый
        пакет {formatNumber(last.price)} +{' '}
        <span className="font-mono text-[13px] font-semibold text-foreground">
          {formatNumber(overPrice)}
        </span>{' '}
        за каждое место сверх {last.upTo}
      </div>
    </div>
  );
};
